/**
 * Impact · Dependencies · Tests · Workspace — in the header, not a right-hand column.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────────────────────
 *
 * These four panels used to live in `MultiAgentRightPanels`, a permanent stacked column on the
 * right. That column is gone: the right edge now belongs to ONE contextual surface — the section a
 * reader opened from the conversation, or the file they opened in the editor — and a four-panel
 * accordion competing for it was the architecture the consolidation set out to remove.
 *
 * The PANELS themselves were never the problem. They read real analysis the backend produced, and
 * deleting them with the column deleted product functionality that nothing replaced. So the entry
 * point moves to the header and the panels come back unchanged, rendered in a popover.
 *
 * ── WHAT THEY READ, AND WHY THAT SURVIVES THE MIGRATION ─────────────────────────────────────
 *
 * Impact, Dependencies and Tests read `multiAgentIdeStore` — a store the consolidation KEEPS,
 * filled by `analysisHydration` from `GET /runs/{id}/detail`'s `analysis` block and by the editor's
 * post-save analysis. Neither path is the legacy SSE, so none of this reintroduces the old runtime.
 *
 * `MultiAgentWorkspacePanel` still reads `multiAgentStore.artifacts`. That is a remaining
 * compatibility bridge, flagged in the consolidation audit as needing its own decision, and it is
 * left exactly as it was rather than quietly rewired here.
 *
 * ── WHAT CHANGED ABOUT THEIR ACTIONS ────────────────────────────────────────────────────────
 *
 * `onSendMessage` used to call `sendMultiAgentMessage`, which created a run through the legacy
 * store. It now FILLS THE COMPOSER, the same interaction the answer's follow-up chips use: one
 * click either way, and the reader keeps the decision to send. That is also what keeps this off the
 * legacy run lifecycle.
 */
import { useState } from "react";
import { BarChart3, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@ncoder/ui";
import { cn } from "@/lib/utils";
import { ImpactPanel } from "@/pages/multi-agent/ide/panels/ImpactPanel";
import { DependenciesPanel } from "@/pages/multi-agent/ide/panels/DependenciesPanel";
import { TestsPanel } from "@/pages/multi-agent/ide/panels/TestsPanel";
import { MultiAgentWorkspacePanel } from "@/pages/multi-agent/ide/panels/MultiAgentWorkspacePanel";

type Tab = "impact" | "dependencies" | "tests" | "workspace";

const TABS: { id: Tab; label: string }[] = [
  { id: "impact", label: "Impact" },
  { id: "dependencies", label: "Dependencies" },
  { id: "tests", label: "Tests" },
  { id: "workspace", label: "Workspace" },
];

export interface WeaveInsightsMenuProps {
  /** Puts text in the composer. NOT a send — see the header note. */
  onAsk: (message: string) => void;
  /** Opens a metadata file in the editor on the right edge. */
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
  analyzingFile?: string | null;
  isBusy?: boolean;
}

export function WeaveInsightsMenu({
  onAsk,
  onOpenFileInEditor,
  analyzingFile,
  isBusy,
}: WeaveInsightsMenuProps) {
  const [tab, setTab] = useState<Tab>("impact");

  /*
   * The panels' own `onSendMessage(msg, displayText)` signature is kept so they are used unmodified.
   * `displayText` was the chat-bubble label while the full prompt went to the backend; with the
   * composer holding the text there is one string and the reader can see all of it, so the full
   * message wins and the label is ignored.
   */
  const send = (message: string) => onAsk(message);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          /*
           * Named for what it contains rather than for the panel it replaced: a reader looking for
           * "what does this change affect" does not know the word `RightPanel`.
           */
          aria-label="Analysis — impact, dependencies, tests and workspace"
          title="Analysis"
          // `min-h-6`: the 24px touch floor (§8.5). 12px text with `py-1` measured 22px.
          className="inline-flex min-h-6 shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BarChart3 aria-hidden className="h-3.5 w-3.5" />
          <span className="max-lg:hidden">Analysis</span>
          <ChevronDown aria-hidden className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      {/*
        WIDTH IS CAPPED AGAINST THE VIEWPORT, not set to a fixed pixel value: these panels carry
        long API names and file paths, and a fixed width would push them off a tablet. The body
        scrolls rather than the popover growing past the screen (§8.5).
      */}
      <DropdownMenuContent
        align="end"
        className="w-[min(92vw,420px)] p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div role="tablist" aria-label="Analysis sections" className="flex items-center gap-1 border-b border-border px-2 py-1.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "rounded px-2 py-1 text-[12px] font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                tab === t.id ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="max-h-[min(70vh,520px)] overflow-y-auto p-1 text-sm">
          {tab === "impact" && (
            <ImpactPanel
              onSendMessage={send}
              onOpenFileInEditor={onOpenFileInEditor}
              analyzingFile={analyzingFile}
              isBusy={isBusy}
            />
          )}
          {tab === "dependencies" && (
            <DependenciesPanel onSendMessage={send} onOpenFileInEditor={onOpenFileInEditor} />
          )}
          {tab === "tests" && (
            <TestsPanel
              onSendMessage={send}
              onOpenFileInEditor={onOpenFileInEditor}
              analyzingFile={analyzingFile}
              isBusy={isBusy}
            />
          )}
          {tab === "workspace" && <MultiAgentWorkspacePanel onOpenFileInEditor={onOpenFileInEditor} />}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
